import { type ElementType, type FC, type PropsWithChildren, type SVGProps } from 'react';

import { type ClassNameMap } from '@mui/material';
import { Icon } from '@vakantiesnl/components/src/atoms/Icon';
import { type MicroCopy } from '@vakantiesnl/types';

import { shape5_6, shape6_7, shape8plus, shapeLowerThan_5, shapeNoScore } from './assets/svg';
import useStyles from './RatingBox.style';

type Rating = {
	from?: number;
	to?: number;
	label: string;
	shapeIcon: FC<SVGProps<SVGSVGElement> & { title?: string }>;
	className: string;
	rating?: number;
};

const getRatingConfig = (styles: ClassNameMap, rating?: number): Rating => {
	const ratingTypes = [
		{
			from: 9,
			to: 10,
			label: 'score.label.phenomenal',
			shapeIcon: shape8plus,
			className: styles.darkGreen,
		},
		{
			from: 8,
			to: 8.9,
			label: 'score.label.fantastic',
			shapeIcon: shape8plus,
			className: styles.darkGreen,
		},
		{
			from: 7,
			to: 7.9,
			label: 'score.label.good',
			shapeIcon: shape6_7,
			className: styles.green,
		},
		{
			from: 5.5,
			to: 6.9,
			label: 'score.label.enough',
			shapeIcon: shape5_6,
			className: styles.yellow,
		},
		{
			from: 4.5,
			to: 5.4,
			label: 'score.label.mediocre',
			shapeIcon: shapeLowerThan_5,
			className: styles.red,
		},
		{
			from: 0,
			to: 4.4,
			label: 'score.label.inadequate',
			shapeIcon: shapeLowerThan_5,
			className: styles.red,
		},
	];

	const defaultResult = {
		label: 'score.label.notAvailable',
		className: styles.gray,
		shapeIcon: shapeNoScore,
		rating: 0,
	};

	if (!rating) {
		return defaultResult;
	}

	const formattedRating = Number(rating.toFixed(1));

	const ratingConfigResult = ratingTypes.filter((v) => formattedRating >= v.from && formattedRating <= v.to);

	if (ratingConfigResult.length) {
		const { label, className, shapeIcon } = ratingConfigResult[0];
		return { label, className, shapeIcon, rating: formattedRating };
	}

	return defaultResult;
};

export interface RatingBoxProps {
	size?: 'xSmall' | 'small' | 'medium' | 'large' | 'jumbo';
	rating: number | undefined;
	label?: boolean;
	component?: ElementType;
	className?: string;
	labelClassName?: string;
	microCopies?: MicroCopy;
}

export const RatingBox: FC<PropsWithChildren<RatingBoxProps>> = ({
	rating,
	label = true,
	size = 'medium',
	children,
	component = 'div',
	className,
	labelClassName,
	microCopies,
}) => {
	const { classes: styles, cx } = useStyles();
	const ratingConfig = getRatingConfig(styles, rating);

	const Component = component;
	const ratingLabel = label && microCopies && microCopies[`${ratingConfig.label}`];

	const sizeClass = {
		[styles.xSmall]: size === 'xSmall',
		[styles.small]: size === 'small',
		[styles.medium]: size === 'medium',
		[styles.large]: size === 'large',
		[styles.jumbo]: size === 'jumbo',
	};

	return (
		<Component className={cx(styles.container, className)} data-qa="rating">
			<span className={cx(ratingConfig.className, styles.rating, sizeClass, 'rating')} data-qa="score">
				<Icon className={styles.shapeIcon} symbol={ratingConfig.shapeIcon} />
				<span className={styles.ratingValue} data-cy="rating-score">
					{ratingConfig.rating}
				</span>
			</span>
			{(children || label) && (
				<span className={cx(styles.wrapper, sizeClass)}>
					{ratingLabel && (
						<span className={cx(ratingConfig.className, styles.label, sizeClass, labelClassName)}>
							{ratingLabel}
						</span>
					)}
					{children}
				</span>
			)}
		</Component>
	);
};
